import { z } from 'zod';
import { uuid, paginationQuery, isoDatetime } from './common.js';

// ─── Enums ────────────────────────────────────────────────────────────────
export const ComplaintStatus = {
  NEW: 'NEW',
  IN_PROGRESS: 'IN_PROGRESS',
  RESOLVED: 'RESOLVED',
  CANCELLED: 'CANCELLED',
} as const;
export type ComplaintStatus = (typeof ComplaintStatus)[keyof typeof ComplaintStatus];

export const ComplaintCategory = {
  FACILITY_DAMAGE: 'FACILITY_DAMAGE',
  CLEANLINESS: 'CLEANLINESS',
  NOISE: 'NOISE',
  LIGHTING: 'LIGHTING',
  SECURITY: 'SECURITY',
  OTHER: 'OTHER',
} as const;
export type ComplaintCategory = (typeof ComplaintCategory)[keyof typeof ComplaintCategory];

export const ComplaintVisibility = {
  PUBLIC: 'PUBLIC',
  PRIVATE: 'PRIVATE',
} as const;
export type ComplaintVisibility =
  (typeof ComplaintVisibility)[keyof typeof ComplaintVisibility];

const STATUSES = [
  ComplaintStatus.NEW,
  ComplaintStatus.IN_PROGRESS,
  ComplaintStatus.RESOLVED,
  ComplaintStatus.CANCELLED,
] as const;
const CATEGORIES = [
  ComplaintCategory.FACILITY_DAMAGE,
  ComplaintCategory.CLEANLINESS,
  ComplaintCategory.NOISE,
  ComplaintCategory.LIGHTING,
  ComplaintCategory.SECURITY,
  ComplaintCategory.OTHER,
] as const;
const VISIBILITIES = [ComplaintVisibility.PUBLIC, ComplaintVisibility.PRIVATE] as const;

const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
const photoUrl = z.string().min(1).max(2_900_000);   // data URL OR https; ~2 MB as base64. Cap to keep D1 happy

export const SortOrder = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
} as const;
export type SortOrder = (typeof SortOrder)[keyof typeof SortOrder];

// ─── Public shape ─────────────────────────────────────────────────────────
export const publicComplaint = z.object({
  id: uuid,
  cluster_id: uuid,
  unit_id: uuid,
  user_id: uuid,
  title: z.string(),
  location_detail: z.string(),
  description: z.string().nullable(),
  date_observed: dateStr,
  category: z.enum(CATEGORIES),
  visibility: z.enum(VISIBILITIES),
  photo_url: z.string().nullable(),
  status: z.enum(STATUSES),
  resolved_photo_url: z.string().nullable(),
  resolved_notes: z.string().nullable(),
  resolved_at: isoDatetime.nullable(),
  resolved_by_user_id: uuid.nullable(),
  cancelled_at: isoDatetime.nullable(),
  in_progress_at: isoDatetime.nullable(),
  created_at: isoDatetime,
  updated_at: isoDatetime,
  // Denormalized for list display — server fills these when joining units/users
  unit_block: z.string().optional(),
  unit_number: z.string().optional(),
  filer_name: z.string().optional(),
});
export type PublicComplaint = z.infer<typeof publicComplaint>;

// ─── Resident requests ────────────────────────────────────────────────────
export const createComplaintRequest = z.object({
  title: z.string().min(1).max(120).trim(),
  location_detail: z.string().min(1).max(300).trim(),
  // Free-form "Catatan". Optional so older clients keep working.
  description: z.string().max(2000).trim().optional(),
  date_observed: dateStr,
  category: z.enum(CATEGORIES).default(ComplaintCategory.OTHER),
  // Private-only by product decision: complaints go to management, never a
  // neighbor-visible feed. The resident app always sends PRIVATE explicitly;
  // this default just makes the API enforce the same thing for any request
  // that omits the field, rather than silently publishing it. The admin panel
  // does not create complaints, so nothing relies on the old PUBLIC default.
  visibility: z.enum(VISIBILITIES).default(ComplaintVisibility.PRIVATE),
  photo_url: photoUrl.optional(),
});
export type CreateComplaintRequest = z.infer<typeof createComplaintRequest>;

export const listComplaintsQuery = paginationQuery.extend({
  status: z.enum(STATUSES).optional(),
  category: z.enum(CATEGORIES).optional(),
  visibility: z.enum(VISIBILITIES).optional(),
  block: z.string().min(1).max(20).optional(),
  this_month: z.coerce.boolean().optional(),
  sort: z.enum([SortOrder.NEWEST, SortOrder.OLDEST]).default(SortOrder.NEWEST),
  mine_only: z.coerce.boolean().optional(),         // residents: just my reports
});
export type ListComplaintsQuery = z.infer<typeof listComplaintsQuery>;

// ─── Staff requests ───────────────────────────────────────────────────────
export const adminListComplaintsQuery = listComplaintsQuery.extend({
  user_id: uuid.optional(),
});
export type AdminListComplaintsQuery = z.infer<typeof adminListComplaintsQuery>;

export const resolveComplaintRequest = z.object({
  resolved_photo_url: photoUrl,                    // mandatory on the staff side
  resolved_notes: z.string().max(500).optional(),
});
export type ResolveComplaintRequest = z.infer<typeof resolveComplaintRequest>;

// ─── Cluster blocks endpoint (powers the block filter chip) ───────────────
export const listBlocksResponse = z.object({
  blocks: z.array(z.string()),
});
